/**
 * Runtime decoders for API responses. The server is ours, but a proxy error
 * page, a half-deployed server or a version skew must surface as a clear
 * BackendError('invalid-response') — not as `undefined` deep inside a screen.
 */
import type { Locale } from '@dropstack/core';
import type {
  LeaderboardEntry,
  LeaderboardPage,
  LeaderboardScope,
  PlayerProfile,
  SchoolOption,
  SubmitResult,
} from '../../contracts.ts';
import { isRecord } from '../../state/storage.ts';
import { isProfileField } from '../profileValidation.ts';
import type {
  CitiesResponse,
  CreateAnonymousPlayerResponse,
  CreateSessionResponse,
  GetMeResponse,
  HealthResponse,
  ProfileFieldProblem,
  SchoolsResponse,
  UpdateMeResponse,
} from './api-types.ts';
import { LEADERBOARD_SCOPES } from './api-types.ts';
import { BackendError } from './errors.ts';

type Obj = Record<string, unknown>;

function fail(what: string): never {
  throw new BackendError('invalid-response', 'invalid-response', `Unexpected response: ${what}`);
}

function obj(x: unknown, what: string): Obj {
  return isRecord(x) ? x : fail(`${what} is not an object`);
}

function str(o: Obj, key: string): string {
  const v = o[key];
  return typeof v === 'string' ? v : fail(`"${key}" is not a string`);
}

function num(o: Obj, key: string): number {
  const v = o[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fail(`"${key}" is not a number`);
}

function bool(o: Obj, key: string): boolean {
  const v = o[key];
  return typeof v === 'boolean' ? v : fail(`"${key}" is not a boolean`);
}

function nullable<T>(o: Obj, key: string, read: (o: Obj, key: string) => T): T | null {
  return o[key] === null || o[key] === undefined ? null : read(o, key);
}

function list(o: Obj, key: string): unknown[] {
  const v = o[key];
  return Array.isArray(v) ? v : fail(`"${key}" is not an array`);
}

function locale(o: Obj, key: string): Locale {
  return o[key] === 'af' ? 'af' : 'en';
}

export function decodeHealth(x: unknown): HealthResponse {
  const o = obj(x, 'health');
  return { ok: bool(o, 'ok'), rulesVersion: num(o, 'rulesVersion'), time: typeof o.time === 'string' ? o.time : '' };
}

export function decodeProfile(x: unknown): PlayerProfile {
  const o = obj(x, 'profile');
  return {
    playerId: str(o, 'playerId'),
    onboarded: bool(o, 'onboarded'),
    displayName: str(o, 'displayName'),
    grade: nullable(o, 'grade', num),
    schoolId: nullable(o, 'schoolId', str),
    schoolName: str(o, 'schoolName'),
    city: str(o, 'city'),
    province: str(o, 'province'),
    country: str(o, 'country'),
    leaderboardOptIn: bool(o, 'leaderboardOptIn'),
    locale: locale(o, 'locale'),
  };
}

export function decodeAnonymousPlayer(x: unknown): CreateAnonymousPlayerResponse {
  const o = obj(x, 'anonymous player');
  return { playerId: str(o, 'playerId'), secret: str(o, 'secret'), profile: decodeProfile(o.profile) };
}

export function decodeMe(x: unknown): GetMeResponse {
  const o = obj(x, 'me');
  return { profile: decodeProfile(o.profile), savedRating: nullable(o, 'savedRating', num) };
}

export function decodeUpdateMe(x: unknown): UpdateMeResponse {
  return { profile: decodeProfile(obj(x, 'update').profile) };
}

function decodeSchool(x: unknown): SchoolOption {
  const o = obj(x, 'school');
  return { id: str(o, 'id'), name: str(o, 'name'), city: str(o, 'city'), province: str(o, 'province'), country: str(o, 'country') };
}

export function decodeSchools(x: unknown): SchoolsResponse {
  return { schools: list(obj(x, 'schools'), 'schools').map(decodeSchool) };
}

export function decodeCities(x: unknown): CitiesResponse {
  const cities = list(obj(x, 'cities'), 'cities');
  return { cities: cities.map((c) => (typeof c === 'string' ? c : fail('city is not a string'))) };
}

export function decodeSession(x: unknown): CreateSessionResponse {
  const o = obj(x, 'session');
  return {
    sessionId: str(o, 'sessionId'),
    token: str(o, 'token'),
    seed: num(o, 'seed'),
    startRating: num(o, 'startRating'),
    referenceRating: num(o, 'referenceRating'),
    rulesVersion: num(o, 'rulesVersion'),
    // Lenient: a server that predates relaxed runs never sends it — and then
    // issued (and will replay) a standard-pace session, whatever was asked.
    relaxed: o.relaxed === true,
  };
}

export function decodeSubmitResult(x: unknown): SubmitResult {
  const o = obj(x, 'submit result');
  const ranksIn = isRecord(o.ranks) ? o.ranks : {};
  const ranks: Partial<Record<LeaderboardScope, number>> = {};
  for (const scope of LEADERBOARD_SCOPES) {
    const v = ranksIn[scope];
    if (typeof v === 'number' && Number.isFinite(v)) ranks[scope] = v;
  }
  const result: SubmitResult = {
    accepted: bool(o, 'accepted'),
    flagged: bool(o, 'flagged'),
    runId: nullable(o, 'runId', str),
    raw: num(o, 'raw'),
    rated: num(o, 'rated'),
    savedRating: num(o, 'savedRating'),
    ranks,
    personalBest: o.personalBest === true,
    // Lenient: a server that predates placement runs never sends the field.
    placement: o.placement === true,
    relaxed: o.relaxed === true,
  };
  if (typeof o.reason === 'string') result.reason = o.reason;
  return result;
}

function decodeEntry(x: unknown): LeaderboardEntry {
  const o = obj(x, 'leaderboard entry');
  const entry: LeaderboardEntry = {
    rank: num(o, 'rank'),
    entryKey: str(o, 'entryKey'),
    displayName: str(o, 'displayName'),
    grade: num(o, 'grade'),
    schoolName: str(o, 'schoolName'),
    city: str(o, 'city'),
    province: str(o, 'province'),
    country: str(o, 'country'),
    score: num(o, 'score'),
    levelReached: num(o, 'levelReached'),
    at: str(o, 'at'),
  };
  if (o.isMe === true) entry.isMe = true;
  return entry;
}

export function decodeLeaderboard(x: unknown): LeaderboardPage {
  const o = obj(x, 'leaderboard');
  return {
    entries: list(o, 'entries').map(decodeEntry),
    me: o.me === null || o.me === undefined ? null : decodeEntry(o.me),
    total: num(o, 'total'),
    source: 'server',
  };
}

/** Parse an ApiErrorBody leniently: any missing piece falls back to generic values. */
export function decodeErrorBody(x: unknown): { code: string; message: string; details: ProfileFieldProblem[] } {
  const error = isRecord(x) && isRecord(x.error) ? x.error : {};
  const details = Array.isArray(error.details)
    ? error.details.flatMap((d): ProfileFieldProblem[] =>
        isRecord(d) && typeof d.field === 'string' && isProfileField(d.field) && typeof d.reason === 'string'
          ? [{ field: d.field, reason: d.reason }]
          : [],
      )
    : [];
  return {
    code: typeof error.code === 'string' ? error.code : 'http-error',
    message: typeof error.message === 'string' ? error.message : '',
    details,
  };
}
